import Button from 'components/Button';
import Field from 'components/Form/Field';
import KeyValue from 'components/Layout/KeyValue';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import Table from 'components/Table/TableFunctional';
import Value from 'components/Value';
import {FC} from 'react';
import {t} from 'ttag';

import {
    CustomTemplateItem,
    CustomTemplateSearchApiResult,
    SearchRequest,
} from '../../services';
import TableCellContent from '../TableCellContent';

const NO_BREAK_SPACE_CHARACTER = '\u00A0';

const columns: {key: keyof CustomTemplateItem}[] = [
    {key: 'partner'},
    {key: 'displayName'},
    {key: 'documentType'},
    {key: 'draft'},
    {key: 'uploadedFileUrl'},
    {key: 'templateFileUrl'},
];

type ResultPanelProps = {
    isLoading: boolean;
    onClear: () => void;
    onSearch: (request: SearchRequest) => void;
    response: CustomTemplateSearchApiResult | undefined;
};

const ResultPanel: FC<ResultPanelProps> = ({
    isLoading,
    onClear,
    onSearch,
    response,
}) => {
    if (!response) return null;

    const {pagination, request, results} = response;
    const {keyword} = request;

    const resultsWithId = results.map((result) => ({
        id: result.mid,
        ...result,
    }));

    const {endCount, startCount, totalCount} = pagination;

    return (
        <div className="flex flex-col gap-y-2">
            <div className="flex gap-x-2">
                <div
                    aria-level={2}
                    className="flex-1 font-semibold"
                    role="heading"
                >
                    {t`Search Result`}
                </div>
                <Button
                    icon="trash"
                    isLoading={isLoading}
                    kind="secondary"
                    onClick={onClear}
                    size="xs"
                >
                    {t`Clear`}
                </Button>
            </div>
            <KeyValue className="mb-4">
                <Field label={t`Keyword`} type="value">
                    <Value
                        value={{
                            type: 'string',
                            value: keyword.replaceAll(
                                /\s/g,
                                NO_BREAK_SPACE_CHARACTER,
                            ),
                        }}
                    />
                </Field>
            </KeyValue>
            <div className="flex flex-col gap-y-4">
                {results.length > 0 && (
                    <div className="flex flex-col gap-y-2">
                        <div className="flex items-end gap-x-2">
                            <div className="subheading flex-1">
                                {t`Showing ${startCount}-${endCount} of ${totalCount}`}
                            </div>
                            <TablePagination
                                hideForSinglePage={true}
                                isAmbigousStop={true}
                                onChange={(page) => onSearch({keyword, page})}
                                onFirst={() => onSearch({keyword, page: 1})}
                                onLast={() =>
                                    onSearch({
                                        keyword,
                                        page: pagination.numPages,
                                    })
                                }
                                pageCurrent={pagination.currentPage}
                                pageStop={pagination.numPages}
                            />
                        </div>
                        <Table>
                            <Table.Header columns={columns}>
                                {(column) => {
                                    const columnLabel = {
                                        displayName: t`Template Name`,
                                        documentType: t`Document Type`,
                                        draft: t`Draft`,
                                        partner: t`Partner`,
                                        templateFileUrl: t`Template File`,
                                        uploadedFileUrl: t`Uploaded File`,
                                    }[column.key as string];

                                    return (
                                        <Table.Column>
                                            {columnLabel}
                                        </Table.Column>
                                    );
                                }}
                            </Table.Header>
                            <Table.Body items={resultsWithId}>
                                {(item) => (
                                    <Table.Row>
                                        {(columnKey) => (
                                            <Table.Cell>
                                                <TableCellContent
                                                    columnKey={
                                                        columnKey as keyof CustomTemplateItem
                                                    }
                                                    item={item}
                                                />
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                )}
                            </Table.Body>
                        </Table>
                    </div>
                )}
                {results.length === 0 && (
                    <div className="flex items-center justify-center rounded-md border border-grey-200 p-6 text-grey-400">
                        {t`No Results`}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResultPanel;
